import { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  CircularProgress,
  Tooltip,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { UpsertMprRequestsAction, GetApproverMprRequestsAction, UpdateApproverMprRequestsAction, GetMprRequestsAction } from "../../../../../store/ATS/MprRequest/Action";
import SelectableSearch from "../../../Base/views/SelectableSearch";
import CommonDatePicker from "../../../Base/views/CommonDatePicker";
import { GetBusinessUnitAction } from "../../../../../store/Ancillary/BusinessUnit/Action";
import Quill from "../../../Base/views/Quill";
import { ConvertDateTime, DateConvertInDbFormat, getAtsUserId } from "../../../Base/helper/baseFunctions";
import { GetMprApproverListHelper, triggerMprEmailNotificationHelper } from "../../helper/AtsMprRequestHelper";
import { GetEmploymentTypeHelper } from "../../../Ancillary/EmploymentType/helper/EmploymentTypeHelper";
import { Mention, MentionsInput } from "react-mentions";
import MentionInputStyles from "../../../ATS/views/ATSCandidate/MentionStyles/MentionInputStyles";
import {LabelWithInfo} from "../../../Base/views/LabelwithInfo";
import ApproverCommentsStatus from "./ApproverCommentsStatus";
import {GetAtsUserHelper} from "../../helper/AtsUserHelper";


const AddMPRPopup = (props) => {
  const { editedRecord, closeMode, isApproveFlow, initialActionType } = props;
  const dispatch = useDispatch();
  const initialState = {
    role: "",
    bu_fk: "",
    location: "",
    headcount: "",
    max_budget: "",
    approver_employee_id: "",
    second_approver_employee_id: "",
    third_approver_employee_id: "",
    justification_comment: "",
    hiring_type: "New Hire",
    is_budgeted: true,
    target_date: "",
    jd: "",
    grade: "",
    min_salary: "",
    max_salary: "",
    min_experience: "",
    max_experience: "",
    additional_comments: "",
    employmenttype_fk: "",
    sub_role: ""
  };
  const [formData, setFormData] = useState(initialState);
  const [jdValue, setJdValue] = useState("");
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [replacementRows, setReplacementRows] = useState([
    { name: "", designation: "", ctc: "" }
  ]);
  const [errors, setErrors] = useState({});
  const [mentionTo, setMentionTo] = useState("");
  const [mentionUsers, setMentionUsers] = useState([]);
  const allMentionUsersRef = useRef([]);

  useEffect(() => {
    if (allMentionUsersRef.current?.length) {
      setMentionUsers(filterUsersByMentionTo(mentionTo, allMentionUsersRef.current));
    }
  }, [mentionTo]);

  // --- Approval Flow States ---
  const [approvalStep, setApprovalStep] = useState(1);
  const [actionType, setActionType] = useState(initialActionType || "");
  const [approvedCount, setApprovedCount] = useState("");
  const [approvedBudget, setApprovedBudget] = useState(0);
  const [remarks, setRemarks] = useState("");
  const { company_logo } = useSelector(state => state?.AncillaryReducer?.LetterHeadReducer);
  
  const initialReplacementList = useMemo(() => {
    const repData = editedRecord?.replacement_details || editedRecord?.mpr_jobpost_replacement_details;
    if (repData) {
      if (typeof repData === "string") {
        try {
          return JSON.parse(repData) || [];
        } catch (e) {
          return [];
        }
      } else if (Array.isArray(repData)) {
        return repData;
      }
    }
    return [];
  }, [editedRecord]);
  const initialLockedCount = initialReplacementList?.length;

  const { isLoadingApproverMprRequests } = useSelector((state) => state?.AtsReducer?.MprRequestReducer || {});
  const isSecondApprover = editedRecord?.mpr_status === "Awaiting Second Approval";
  const isThirdApprover = editedRecord?.mpr_status === "Awaiting Third Approval";
  const defaultHeadcount = (isSecondApprover || isThirdApprover) ? (editedRecord?.approved_headcount || editedRecord?.headcount) : editedRecord?.headcount;
  const fallbackMaxBudget = editedRecord?.max_budget || (Number(editedRecord?.headcount || 0) * Number(editedRecord?.max_salary || editedRecord?.approver_max_salary || 0));
  const fallbackApprovedBudget = editedRecord?.approver_budget || editedRecord?.approved_budget || fallbackMaxBudget;
  const defaultBudget = (isSecondApprover || isThirdApprover) ? fallbackApprovedBudget : fallbackMaxBudget;
  const salaryPerHead = Number(editedRecord?.max_salary || editedRecord?.approver_max_salary || (Number(editedRecord?.max_budget || 0) / Number(editedRecord?.headcount || 1)) || 0);
  const mprMeta = typeof editedRecord?.mpr_metadata_json === 'string'
    ? (() => { try { return JSON.parse(editedRecord.mpr_metadata_json); } catch(e) { return {}; } })()
    : (editedRecord?.mpr_metadata_json || {});

  const firstApprovedCountDisplay = mprMeta?.first_approved_count ?? (isSecondApprover ? (editedRecord?.approved_headcount || editedRecord?.headcount) : editedRecord?.headcount) ?? "";
  const firstApprovedBudgetDisplay = mprMeta?.first_approved_budget ?? (isSecondApprover ? (editedRecord?.approver_budget || editedRecord?.approved_budget || fallbackMaxBudget) : fallbackMaxBudget) ?? "";
  const secondApprovedCountDisplay = mprMeta?.second_approved_count ?? (editedRecord?.approved_headcount || editedRecord?.headcount) ?? "";
  const secondApprovedBudgetDisplay = mprMeta?.second_approved_budget ?? (editedRecord?.approver_budget || editedRecord?.approved_budget || fallbackMaxBudget) ?? "";

  useEffect(() => {
    if (actionType === "Approved") {
      setApprovedCount(defaultHeadcount || "");
      setApprovedBudget(defaultBudget || 0);
    }
    // eslint-disable-next-line
  }, [actionType, editedRecord]);

  const handleApproveAction = () => {
    if (actionType === "Approved" && !approvedCount) {
        setErrors({ ...errors, approvedCount: "Approved count is required" });
        return;
    }
    const loginEmployeeId = getAtsUserId();
    const updatedMprMetadataJson = editedRecord?.mpr_metadata_json ? { ...editedRecord.mpr_metadata_json } : {};
    let finalStatus = actionType;

    if (+loginEmployeeId === +editedRecord?.approver_id) {
      if (actionType === "Approved") {
        updatedMprMetadataJson.first_approved_count = Number(approvedCount);
        updatedMprMetadataJson.first_approved_budget = Number(approvedBudget);
        if (editedRecord?.second_approver_employee_id) {
          finalStatus = "Awaiting Second Approval";
        } else if (editedRecord?.third_approver_employee_id) {
          finalStatus = "Awaiting Third Approval";
        } else {
          finalStatus = "Approved";
        }
      } else {
        finalStatus = "Rejected";
      }
      updatedMprMetadataJson.first_comment = remarks;
      updatedMprMetadataJson.first_status = actionType;
      if (actionType === "Rejected") {
        updatedMprMetadataJson.second_status = "Not Required";
        updatedMprMetadataJson.third_status = "Not Required";
      }
    } else if (+loginEmployeeId === +editedRecord?.second_approver_employee_id) {
      if (actionType === "Approved") {
        updatedMprMetadataJson.second_approved_count = Number(approvedCount);
        updatedMprMetadataJson.second_approved_budget = Number(approvedBudget);
        if (editedRecord?.third_approver_employee_id) {
          finalStatus = "Awaiting Third Approval";
        } else {
          finalStatus = "Approved";
        }
      } else {
        finalStatus = "Rejected";
      }
      updatedMprMetadataJson.second_comment = remarks;
      updatedMprMetadataJson.second_status = actionType;
      if (actionType === "Rejected") {
        updatedMprMetadataJson.third_status = "Not Required";
      }
    } else if (+loginEmployeeId === +editedRecord?.third_approver_employee_id) {
      finalStatus = actionType;
      if (actionType === "Approved") {
        updatedMprMetadataJson.third_approved_count = Number(approvedCount);
        updatedMprMetadataJson.third_approved_budget = Number(approvedBudget);
      }
      updatedMprMetadataJson.third_comment = remarks;
      updatedMprMetadataJson.third_status = actionType;
    }

    const activeReplacements = replacementRows.filter(
      (r) => (r.name && r.name.trim() !== "") || (r.designation && r.designation.trim() !== "") || (r.ctc && String(r.ctc).trim() !== "")
    );

    const payload = {
      mpr_request_fk: editedRecord?.mpr_requests_pk,
      status: finalStatus,
      approved_positions_count: actionType === "Approved" ? Number(approvedCount) : null,
      mpr_metadata_json: JSON.stringify(updatedMprMetadataJson),
      grade: editedRecord?.grade || "",
      approved_budget: actionType === "Approved" ? Number(approvedBudget) : null,
      replacement_details:
        editedRecord?.hiring_type === "Replacement" && activeReplacements.length > 0
          ? JSON.stringify(activeReplacements)
          : null,
    };    
    dispatch(
      UpdateApproverMprRequestsAction(payload, (res, err) => {
        if (res && !err) {
          toast.dismiss();
          toast.success(actionType === "Approved" ? "MPR Approved Successfully" : "MPR Rejected Successfully");
          dispatch(GetApproverMprRequestsAction({ all_status: false }));
          dispatch(GetMprRequestsAction());
          closeMode();
          const updatedRec = (res?.data?.data || [])?.find((rec) => +rec?.mpr_requests_pk ===  +editedRecord?.mpr_requests_pk);
          const updatedRecord = {
            ...editedRecord,
            ...updatedRec,
          };
          // Trigger email notifications based on approver action
          if (actionType === "Rejected") {
            let approverLevel = "first-level";
            let rejectedByName = editedRecord?.approver_name;

            if (+loginEmployeeId === +editedRecord?.second_approver_employee_id) {
              approverLevel = "second-level";
              rejectedByName = editedRecord?.second_approver_emp_name;
            } else if (+loginEmployeeId === +editedRecord?.third_approver_employee_id) {
              approverLevel = "third-level";
              rejectedByName = editedRecord?.third_approver_emp_name;
            }

            triggerMprEmailNotificationHelper({
              scenario: "REJECTED",
              record: updatedRecord,
              approverLevel,
              rejectedByName,
              remarks,
              companyLogo: company_logo,
            });
          } else if (actionType === "Approved") {
            if (+loginEmployeeId === +editedRecord?.approver_id) {
              if (editedRecord?.second_approver_employee_id) {
                triggerMprEmailNotificationHelper({
                  scenario: "FIRST_APPROVER_APPROVED",
                  record: updatedRecord,
                  companyLogo: company_logo,
                });
              } else {
                triggerMprEmailNotificationHelper({
                  scenario: "FINAL_APPROVED",
                  record: updatedRecord,
                  finalApproverName: editedRecord?.approver_name,
                  companyLogo: company_logo,
                });
              }
            } else if (+loginEmployeeId === +editedRecord?.second_approver_employee_id) {
              if (editedRecord?.third_approver_employee_id) {
                triggerMprEmailNotificationHelper({
                  scenario: "SECOND_APPROVER_APPROVED",
                  record: updatedRecord,
                  companyLogo: company_logo,
                });
              } else {
                triggerMprEmailNotificationHelper({
                  scenario: "FINAL_APPROVED",
                  record: updatedRecord,
                  finalApproverName: editedRecord?.second_approver_emp_name,
                  companyLogo: company_logo,
                });
              }
            } else if (+loginEmployeeId === +editedRecord?.third_approver_employee_id) {
              triggerMprEmailNotificationHelper({
                scenario: "FINAL_APPROVED",
                record: updatedRecord,
                finalApproverName: editedRecord?.third_approver_emp_name,
                companyLogo: company_logo,
              });
            }
          }
        } else if (err) {
          toast.dismiss();
          toast.error(err?.response?.data?.error?.[0]?.message || "Failed to update MPR Status");
        }
      }),
    );
  };
  // ------------------------------

  const validate = (fieldValues) => {
    let temp = { ...errors };
    if ("role" in fieldValues) {
      temp.role = !fieldValues.role ? "Role is required" : "";
    }
    if ("bu_fk" in fieldValues) {
      temp.bu_fk = !fieldValues.bu_fk ? "Department is required" : "";
    }
    if ("headcount" in fieldValues) {
      if (!fieldValues.headcount) {
        temp.headcount = "Headcount is required";
      } else if (Number(fieldValues.headcount) <= 0) {
        temp.headcount = "Headcount must be greater than 0";
      } else if (
        (fieldValues.hiring_type === "Replacement" || formData.hiring_type === "Replacement") &&
        Number(fieldValues.headcount) > 5
      ) {
        temp.headcount = "Maximum replacement headcount is 5";
      } else {
        temp.headcount = "";
      }
    }
    if (fieldValues.is_budgeted) {
      if ("max_budget" in fieldValues) {
        temp.max_budget = !fieldValues.max_budget ? "Budget is required" : "";
      }
    } else {
      temp.max_budget = "";
    }
    if ("hiring_type" in fieldValues) {
      temp.hiring_type = !fieldValues.hiring_type
        ? "Hiring Type is required"
        : "";
    }
    if ("approver_employee_id" in fieldValues) {
      temp.approver_employee_id = !fieldValues.approver_employee_id
        ? "Approver is required"
        : "";
    }
    if (fieldValues.third_approver_employee_id && !fieldValues.second_approver_employee_id) {
      temp.second_approver_employee_id = "Second Approver is required before selecting Third Approver";
    } else if ("second_approver_employee_id" in fieldValues) {
      temp.second_approver_employee_id = "";
    }
    if ("max_salary" in fieldValues && fieldValues?.is_budgeted) {
      temp.max_salary = !fieldValues.max_salary
        ? "Max Salary is required" : "";
    } else temp.max_salary = "";

    setErrors({ ...temp });

    return Object.values(temp).every((x) => x === "");
  };
  const handleChange = (event, value) => {
    if (["bu_fk", "approver_employee_id", "second_approver_employee_id", "third_approver_employee_id", "employmenttype_fk"].includes(event)) {
      if (value) {
        const isApproveer = ["approver_employee_id", "second_approver_employee_id", "third_approver_employee_id"].includes(event);
        setFormData({ ...formData, [event]: value });
        isApproveer && setEmployeesData({ load: false, values: [] });
      } else {
        setFormData({ ...formData, [event]: "" });
      }
      Object.values?.(errors)?.find((res) => res !== "")?.length > 0 &&
        validate({ ...formData, [event]: value ? value : "" });
    } else if (event.target.name === "is_budgeted") {
      setFormData({ ...formData, 
        [event.target.name]: event.target.value, 
        max_budget: event.target.max_budget
      });
      Object.values?.(errors)?.find((res) => res !== "")?.length > 0 &&
        validate({ ...formData, [event.target.name]: event.target.value, max_budget: event.target.max_budget });
    } else {
      let newFormData = {
        ...formData,
        [event.target.name]: event.target.value,
      };

      if (event.target.name === "headcount" && formData.hiring_type === "Replacement") {
        if (Number(event.target.value) > 5) {
          event.target.value = 5;
          newFormData.headcount = 5;
        }
      }

      // Auto-calculate budget if headcount or max_salary changes
      if (event.target.name === "headcount" || event.target.name === "max_salary") {
        const hc = event.target.name === "headcount" ? event.target.value : formData.headcount;
        const maxSal = event.target.name === "max_salary" ? event.target.value : formData.max_salary;
        if (hc && maxSal && newFormData.is_budgeted) {
            newFormData.max_budget = Number(hc) * Number(maxSal);
        }
      }

      setFormData(newFormData);
      Object.values?.(errors)?.find((res) => res !== "")?.length > 0 &&
        validate(newFormData);
    }
  };
  const handleMentionChange = (e, name) => {
    const inputValue = e.target.value;
    const endsWithSpace = inputValue.endsWith(" ");
    if (endsWithSpace) {
      const trimmedValue = inputValue.trim();
      const updatedValue = trimmedValue + ",";
      setMentionTo(updatedValue);
    } else {
      setMentionTo(inputValue);
    }
  };
  const handleAddReplacementRow = () => {
    const maxLimit = editedRecord?.mpr_requests_pk
      ? Math.min(5, Math.max(1, Number(approvedCount) || 1))
      : (formData.hiring_type === "Replacement" ? Math.min(5, Math.max(1, Number(formData.headcount) || 1)) : Math.max(1, Number(formData.headcount) || 1));
    if (replacementRows.length < maxLimit) {
      setReplacementRows([...replacementRows, { name: "", designation: "", ctc: "" }]);
    }
  };

  const handleRemoveReplacementRow = (index) => {
    if (editedRecord?.mpr_requests_pk && index < initialLockedCount) {
      return;
    }
    if (replacementRows.length > 1) {
      setReplacementRows(replacementRows.filter((_, idx) => idx !== index));
    } else {
      setReplacementRows([{ name: "", designation: "", ctc: "" }]);
    }
  };

  const handleReplacementRowChange = (index, field, value) => {
    const updated = [...replacementRows];
    updated[index] = { ...updated[index], [field]: value };
    setReplacementRows(updated);
  };

  const { businessunitData } = useSelector(
    (state) => state?.AncillaryReducer?.BusinessUnitReducer,
  );
 
  const handleSubmit = () => {
    if (formData.third_approver_employee_id && !formData.second_approver_employee_id) {
      toast.dismiss();
      toast.error("Second Approver is required before selecting Third Approver");
      setErrors((prev) => ({
        ...prev,
        second_approver_employee_id: "Second Approver is required before selecting Third Approver",
      }));
      return;
    }
    if (!validate(formData)) return;
    const loginEmployeeId = getAtsUserId();
    const isFirstApproverCreator = !editedRecord && (+loginEmployeeId === +formData?.approver_employee_id?.ats_users_id);

    const approverStatus = editedRecord
      ? {
          first_status: "Cancelled",
          first_comment: "",
          second_status: "Cancelled",
          second_comment: "",
          third_status: "Cancelled",
          third_comment: "",
        }
      : {
          first_status: isFirstApproverCreator ? "Approved" : "Pending",
          first_comment: isFirstApproverCreator ? "Auto-approved (requested by first approver)" : "",
          first_approved_count: isFirstApproverCreator ? Number(formData?.headcount) : undefined,
          first_approved_budget: isFirstApproverCreator && formData?.is_budgeted ? Number(formData?.max_budget) : undefined,
          second_status: "Pending",
          second_comment: "",
          third_status: "Pending",
          third_comment: "",
        };

    let initialStatus = editedRecord ? "Cancelled" : "Submitted";
    if (isFirstApproverCreator) {
      if (formData?.second_approver_employee_id?.ats_users_id) {
        initialStatus = "Awaiting Second Approval";
      } else if (formData?.third_approver_employee_id?.ats_users_id) {
        initialStatus = "Awaiting Third Approval";
      } else {
        initialStatus = "Approved";
      }
    }

    const activeReplacements = replacementRows.filter(
      (r) => (r.name && r.name.trim() !== "") || (r.designation && r.designation.trim() !== "") || (r.ctc && String(r.ctc).trim() !== "")
    );

    const payload = {
      mpr_request_fk: editedRecord ? editedRecord?.mpr_requests_pk : null,
      status: initialStatus,
      role: formData?.role,
      bu_fk: formData?.bu_fk?.businessunit_id,
      location: formData?.location || "",
      headcount: Number(formData?.headcount),
      max_budget: formData?.is_budgeted ? Number(formData?.max_budget) : null,
      approver_employee_id: formData?.approver_employee_id?.ats_users_id,
      second_approver_employee_id:
        formData?.second_approver_employee_id?.ats_users_id || null,
      third_approver_employee_id:
        formData?.third_approver_employee_id?.ats_users_id || null,
      justification_comment: formData?.justification_comment || "",
      hiring_type: formData?.hiring_type,
      is_budgeted: formData?.is_budgeted,
      target_date: formData?.target_date || null,
      jd: jdValue || formData?.jd || "",
      mpr_metadata_json: JSON.stringify(approverStatus),
      grade: formData?.grade || "",
      min_salary: formData?.min_salary ? Number(formData?.min_salary) : null,
      max_salary: formData?.max_salary ? Number(formData?.max_salary) : null,
      min_experience: formData?.min_experience ? Number(formData?.min_experience) : null,
      max_experience: formData?.max_experience ? Number(formData?.max_experience) : null,
      additional_comments: formData?.additional_comments || "",
      employmenttype_fk: formData?.employmenttype_fk?.employmenttype_id || "",
      replacement_details:
        formData?.hiring_type === "Replacement" && activeReplacements.length > 0
          ? JSON.stringify(activeReplacements)
          : null,
      sub_role: formData?.sub_role,
      hr_leads: mentionTo,
    };

    dispatch(
      UpsertMprRequestsAction(payload, (res, err) => {
        if (res && !err) {
          toast.dismiss();
          toast.success(editedRecord ? "MPR Cancelled Successfully" : "MPR Request Submitted Successfully");
          dispatch(GetMprRequestsAction());
          closeMode();

          // Send email notification on successful new MPR creation
          if (!editedRecord) {
            const createdRecord = res?.data?.data[0] || {}; // change if the newly created request is not coming at first index [0]

            const mprRecord = { ...createdRecord };

            if (isFirstApproverCreator) {
              if (formData?.second_approver_employee_id?.ats_users_id) {
                triggerMprEmailNotificationHelper({
                  scenario: "FIRST_APPROVER_APPROVED",
                  record: mprRecord,
                  companyLogo: company_logo,
                });
              } else {
                triggerMprEmailNotificationHelper({
                  scenario: "FINAL_APPROVED",
                  record: mprRecord,
                  finalApproverName: createdRecord?.mpr_owner_email ,
                  companyLogo: company_logo,
                });
              }
            } else {
              triggerMprEmailNotificationHelper({
                scenario: "CREATED",
                record: mprRecord,
                companyLogo: company_logo,
              });
            }
          }
        } else if (err) {
          toast.dismiss();
          toast.error(err?.response?.data?.error?.[0]?.message || "Failed to submit MPR Request");
        }
      }),
    );
  };

  useEffect(() => {
    dispatch(GetBusinessUnitAction());
    GetMprApproverListHelper().then(res => {
      if(res?.data?.statuscode === 200) {
        const list = res?.data?.data?.[0]?.approver_json || [];
        setEmployeeOptions(list);
        if (!editedRecord?.mpr_requests_pk) {
          const uniqueUsers = getUniqueUsers(res?.data?.data?.[0]);
            const adminUserEmails = (res?.data?.data?.[0]?.owner_admin_json || [])?.map(user => user?.email_id).filter(Boolean).join(",");
            setMentionTo(adminUserEmails); // auto populate only the Admin in HR Lead field
      
            const formatted = uniqueUsers
                  ?.map((emp) => {
                    const email = emp?.email_id;
                    if (!email) return null;
                    return {
                      ...emp,
                      id: email,
                      email_id: email,
                      display: emp?.name ? `${emp?.name} (${email})` : email,
                    };
                  })
                  ?.filter(Boolean);
            allMentionUsersRef.current = formatted;
            const currentEmails = !editedRecord?.mpr_requests_pk ? adminUserEmails : (editedRecord?.hr_leads || "");
            setMentionUsers(filterUsersByMentionTo(currentEmails, formatted));
          
        }
      }
    })
    GetEmploymentTypeHelper({}).then(res => {
      if (res?.data?.statuscode === 200) {
        setEmploymentTypeData({ load: false, values: res?.data?.data || [] });
      } else {
        setEmploymentTypeData({ load: false, values: [] });
      }
    }).catch(() => setEmploymentTypeData({ load: false, values: [] }));
  
  // eslint-disable-next-line
  }, [dispatch]);

  useEffect(() => {
    if (editedRecord?.mpr_requests_pk) {
      let repList = [];
      const repData = editedRecord?.replacement_details || editedRecord?.mpr_jobpost_replacement_details;
      if (repData) {
        if (typeof repData === "string") {
          try {
            repList = JSON.parse(repData) || [];
          } catch (e) {
            repList = [];
          }
        } else if (Array.isArray(repData)) {
          repList = repData;
        }
      }
      setReplacementRows(repList.length > 0 ? repList : [{ name: "", designation: "", ctc: "" }]);
      setFormData({
        ...editedRecord,
        status: editedRecord?.mpr_status,
        bu_fk: {
          businessunit_id: editedRecord?.bu_id,
          name: editedRecord?.bu_name,
        },
        approver_employee_id: {
          ats_users_id: editedRecord?.approver_id,
          name: editedRecord?.approver_name,
        },
        second_approver_employee_id: editedRecord?.second_approver_employee_id
          ? {
              ats_users_id: editedRecord?.second_approver_employee_id,
              name: editedRecord?.second_approver_emp_name || "",
            }
          : "",
        third_approver_employee_id: editedRecord?.third_approver_employee_id
          ? {
              ats_users_id: editedRecord?.third_approver_employee_id,
              name: editedRecord?.third_approver_emp_name || "",
            }
          : "",
        hiring_type: editedRecord?.hiring_type || "New Hire",
        is_budgeted: editedRecord?.is_budgeted !== false,
        justification_comment: editedRecord?.justification_comment || "",
        additional_comments: editedRecord?.additional_comments || "",
        employmenttype_fk: editedRecord?.employmenttype_fk
          ? {
              employmenttype_id: editedRecord?.employmenttype_fk,
              etype_name: editedRecord?.etype_name || editedRecord?.employmenttype_name || "",
            }
          : "",
      });
      setJdValue(editedRecord?.jd || "");
      setMentionTo(editedRecord?.hr_leads || "");
    } else {
      setJdValue("");
    }

  }, [editedRecord]);
  const [typeData, setTypeData] = useState({
    load: true,
    values: [],
  });
  const [employmentTypeData, setEmploymentTypeData] = useState({
    load: true,
    values: [],
  });
  const [employeesData, setEmployeesData] = useState({
    load: true,
    values: [],
  });
  const handleType = () => {
    businessunitData && setTypeData({ load: false, values: businessunitData });
  };
  const handleEmployees = (type) => {
    // Approver can be Admin and Owner only
    if (employeeOptions) {
      if (type === "approver") {
        const excludeIds = [
          formData?.second_approver_employee_id?.ats_users_id,
          formData?.third_approver_employee_id?.ats_users_id,
        ].filter(Boolean);
        const filtered = employeeOptions?.filter(
          (el) => !excludeIds.includes(el?.ats_users_id),
        );
        setEmployeesData({
          load: false,
          values: filtered,
        });
      } else if (type === "second_approver") {
        const excludeIds = [
          formData?.approver_employee_id?.ats_users_id,
          formData?.third_approver_employee_id?.ats_users_id,
        ].filter(Boolean);
        const filtered = employeeOptions?.filter(
          (el) => !excludeIds.includes(el?.ats_users_id),
        );
        setEmployeesData({
          load: false,
          values: filtered,
        });
      } else if (type === "third_approver") {
        const excludeIds = [
          formData?.approver_employee_id?.ats_users_id,
          formData?.second_approver_employee_id?.ats_users_id,
        ].filter(Boolean);
        const filtered = employeeOptions?.filter(
          (el) => !excludeIds.includes(el?.ats_users_id),
        );
        setEmployeesData({
          load: false,
          values: filtered,
        });
      } else {
        setEmployeesData({
          load: false,
          values: employeeOptions,
        });
      }
    }
  };
  const isViewMode = Boolean(editedRecord?.mpr_requests_pk);

  return (
    <Box>
      <Box sx={{ display: approvalStep === 1 ? "block" : "none" }}>
      <Grid container spacing={2} sx={isViewMode ? { opacity: 0.95 } : {}}>
        {/* 1. Hiring Type */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel id="hiring-type-label">Hiring Type</InputLabel>
            <Select
              labelId="hiring-type-label"
              label="Hiring Type"
              name="hiring_type"
              disabled={isViewMode}
              value={formData?.hiring_type}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  hiring_type: e.target.value,
                });
              }}
            >
              <MenuItem value="New Hire">New Hire</MenuItem>
              <MenuItem value="Replacement">Replacement</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* 1b. Employment Type */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <SelectableSearch
              isOptionEqualToValue={(option, value) =>
                +option.employmenttype_id === +value.employmenttype_id
              }
              label="Employment Type"
              value={formData?.employmenttype_fk}
              loading={() => {}}
              fieldLabel="etype_name"
              name="employmenttype_fk"
              size="small"
              disabled={isViewMode}
              onChangeEvent={handleChange}
              data={employmentTypeData}
            />
          </FormControl>
        </Grid>

        {/* 2. Designation */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <TextField
              fullWidth
              required
              label="Designation"
              name="role"
              disabled={isViewMode}
              value={formData?.role}
              onChange={handleChange}
              size="small"
              {...(errors?.role && {
                error: true,
                helperText: errors?.role,
              })}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <TextField
              fullWidth
              label="Role"
              name="sub_role"
              disabled={isViewMode}
              value={formData?.sub_role}
              onChange={handleChange}
              size="small"
              {...(errors?.sub_role && {
                error: true,
                helperText: errors?.sub_role,
              })}
            />
          </FormControl>
        </Grid>

        {/* 3. Department */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <SelectableSearch
              isOptionEqualToValue={(option, value) =>
                +option.businessunit_id === +value.businessunit_id
              }
              label="Department"
              value={formData?.bu_fk}
              loading={() => handleType()}
              fieldLabel="name"
              name="bu_fk"
              size="small"
              disabled={isViewMode}
              onChangeEvent={handleChange}
              data={typeData}
              required
              {...(errors?.bu_fk && {
                error: true,
                helperText: errors?.bu_fk,
              })}
            />
          </FormControl>
        </Grid>

        {/* 4. Headcount */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <TextField
              fullWidth
              required
              type="number"
              label={editedRecord ? "Requested Headcount" : "Headcount"}
              name="headcount"
              disabled={isViewMode}
              value={formData?.headcount}
              onChange={handleChange}
              size="small"
              inputProps={{
                min: 1,
                ...(formData?.hiring_type === "Replacement" ? { max: 5 } : {}),
              }}
              InputProps={{
                endAdornment: formData?.hiring_type === "Replacement" ? (
                  <InputAdornment position="end">
                    <Tooltip title="Maximum replacement headcount is 5" arrow>
                      <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary", cursor: "pointer" }} />
                    </Tooltip>
                  </InputAdornment>
                ) : undefined,
              }}
              {...(errors?.headcount && {
                error: true,
                helperText: errors?.headcount,
              })}
            />
          </FormControl>
        </Grid>

        {/* 5. Min Salary */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <TextField
              fullWidth
              type="number"
              label="Min Salary"
              name="min_salary"
              disabled={isViewMode}
              value={formData?.min_salary}
              onChange={handleChange}
              size="small"
            />
          </FormControl>
        </Grid>

        {/* 6. Max Salary */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <TextField
              required={formData?.is_budgeted}
              fullWidth
              type="number"
              label="Max Salary"
              name="max_salary"
              disabled={isViewMode}
              value={formData?.max_salary}
              onChange={handleChange}
              size="small"
              {...(errors?.max_salary && {
                error: true,
                helperText: errors?.max_salary,
              })}
            />
          </FormControl>
        </Grid>

        {/* 7. Min Experience */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <TextField
              fullWidth
              type="number"
              label="Min Experience"
              name="min_experience"
              disabled={isViewMode}
              value={formData?.min_experience}
              onChange={handleChange}
              size="small"
            />
          </FormControl>
        </Grid>

        {/* 8. Max Experience */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <TextField
              fullWidth
              type="number"
              label="Max Experience"
              name="max_experience"
              disabled={isViewMode}
              value={formData?.max_experience}
              onChange={handleChange}
              size="small"
            />
          </FormControl>
        </Grid>

        {/* 9. Grade */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <TextField
              fullWidth
              label="Grade"
              name="grade"
              disabled={isViewMode}
              value={formData?.grade}
              onChange={handleChange}
              size="small"
            />
          </FormControl>
        </Grid>

        {/* 10. Budget Status */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel id="budget-status-label">Budget Status</InputLabel>
            <Select
              labelId="budget-status-label"
              label="Budget Status"
              name="is_budgeted"
              disabled={isViewMode}
              value={formData?.is_budgeted}
              onChange={(e) => {
                const isBudgeted = e.target.value === "true" || e.target.value === true;
                let newMaxBudget = formData.max_budget;
                
                if (isBudgeted && formData.headcount && formData.max_salary) {
                  newMaxBudget = Number(formData.headcount) * Number(formData.max_salary);
                } else if (!isBudgeted) {
                  newMaxBudget = "";
                }
                handleChange({target: {name: "is_budgeted", value: isBudgeted, max_budget: newMaxBudget}})
              }}
            >
              <MenuItem value={true}>Budgeted</MenuItem>
              <MenuItem value={false}>Not Budgeted</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* 11. Budget Input (conditionally visible) */}
        {formData?.is_budgeted && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <TextField
                fullWidth
                required
                type="number"
                label={editedRecord ? "Requested Budget" : "Budget Amount"}
                name="max_budget"
                disabled={isViewMode}
                value={formData?.max_budget}
                onChange={handleChange}
                size="small"
                {...(errors?.max_budget && {
                  error: true,
                  helperText: errors?.max_budget,
                })}
              />
            </FormControl>
          </Grid>
        )}

        {/* 12. Target Date for Hiring */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <CommonDatePicker
              name="target_date"
              label="Target Date for Hiring"
              pickerFormat="dd-MM-yyyy"
              disabled={isViewMode}
              DefaultDTvalue={
                formData?.target_date ? new Date(formData?.target_date) : null
              }
              handleChange={(value) => {
                handleChange({
                  target: {
                    name: "target_date",
                    value: value ? DateConvertInDbFormat(value) : "",
                  },
                });
              }}
              size="small"
              required={false}
              error={!!errors.target_date}
              helperText={errors.target_date}
              onKeyDown={(e) => e.preventDefault()}
              onKeyPress={(e) => e.preventDefault()}
            />
          </FormControl>
        </Grid>

        {/* 13. Location */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <TextField
              fullWidth
              label="Location"
              name="location"
              disabled={isViewMode}
              value={formData?.location}
              onChange={handleChange}
              size="small"
            />
          </FormControl>
        </Grid>

        {/* 14. Approver */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <SelectableSearch
              label="Approver"
              value={formData?.approver_employee_id}
              loading={() => handleEmployees("approver")}
              fieldLabel="name"
              required={true}
              variant={"outlined"}
              name="approver_employee_id"
              size="small"
              disabled={isViewMode}
              onChangeEvent={handleChange}
              data={employeesData}
              {...(errors?.approver_employee_id && {
                error: true,
                helperText: errors?.approver_employee_id,
              })}
            />
          </FormControl>
        </Grid>

        {/* 15. Second Approver */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <SelectableSearch
              label="Second Approver"
              value={formData?.second_approver_employee_id}
              loading={() => handleEmployees("second_approver")}
              fieldLabel="name"
              required={false}
              variant={"outlined"}
              name="second_approver_employee_id"
              size="small"
              disabled={isViewMode}
              onChangeEvent={handleChange}
              data={employeesData}
              {...(errors?.second_approver_employee_id && {
                error: true,
                helperText: errors?.second_approver_employee_id,
              })}
            />
          </FormControl>
        </Grid>

        {/* 15b. Third Approver */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <SelectableSearch
              label="Third Approver"
              value={formData?.third_approver_employee_id}
              loading={() => handleEmployees("third_approver")}
              fieldLabel="name"
              required={false}
              variant={"outlined"}
              name="third_approver_employee_id"
              size="small"
              disabled={isViewMode}
              onChangeEvent={handleChange}
              data={employeesData}
            />
          </FormControl>
        </Grid>
        {(editedRecord?.createdon || editedRecord?.mpr_owner_id) && (
          <Grid container item xs={12} spacing={2}>
          {editedRecord?.mpr_owner_name && <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <TextField
                fullWidth
                label="Created By"
                name="created_by"
                disabled={isViewMode}
                value={editedRecord?.mpr_owner_name}
                size="small"
              />
            </FormControl>
          </Grid>}
          {editedRecord?.createdon && <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <TextField
                fullWidth
                label="Created On"
                name="created_on"
                disabled={isViewMode}
                value={ConvertDateTime(editedRecord?.createdon)}
                size="small"
              />
            </FormControl>
          </Grid>}
          </Grid>
        )}

        {/* Replacement Details Dynamic Table (Before JD & Roles) */}
        {formData?.hiring_type === "Replacement" && (
          <Grid item xs={12} sx={{ mt: 1, mb: 1 }}>
            <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>
                  Replacement Details
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: "12px", color: "#64748b" }}>
                    (Max up to headcount)
                  </Typography>
                  {!editedRecord?.mpr_requests_pk && (
                    <Tooltip
                      title={
                        replacementRows.length >= Math.max(1, Number(formData.headcount) || 1)
                          ? `Maximum limit reached (${Math.max(1, Number(formData.headcount) || 1)})`
                          : "Add employee up to headcount"
                      }
                      arrow
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={replacementRows.length >= Math.max(1, Number(formData.headcount) || 1)}
                          onClick={handleAddReplacementRow}
                          sx={{ p: 0.5 }}
                        >
                          <AddCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "6px" }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: "60px", color: "#475569" }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Current Designation</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Current CTC</TableCell>
                      {!editedRecord?.mpr_requests_pk && (
                        <TableCell sx={{ fontWeight: 600, width: "80px", textAlign: "center", color: "#475569" }}>Action</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {replacementRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ color: "#64748b", fontWeight: 500 }}>{idx + 1}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            disabled={!!editedRecord?.mpr_requests_pk}
                            placeholder="Enter name"
                            value={row.name || ""}
                            onChange={(e) => handleReplacementRowChange(idx, "name", e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            disabled={!!editedRecord?.mpr_requests_pk}
                            placeholder="Enter current designation"
                            value={row.designation || ""}
                            onChange={(e) => handleReplacementRowChange(idx, "designation", e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            disabled={!!editedRecord?.mpr_requests_pk}
                            placeholder="Enter current CTC"
                            value={row.ctc || ""}
                            onChange={(e) => handleReplacementRowChange(idx, "ctc", e.target.value)}
                          />
                        </TableCell>
                        {!editedRecord?.mpr_requests_pk && (
                          <TableCell sx={{ textAlign: "center" }}>
                            <Tooltip
                              title={replacementRows.length <= 1 ? "At least one replacement record is required" : "Remove Row"}
                              arrow
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={replacementRows.length <= 1}
                                  onClick={() => handleRemoveReplacementRow(idx)}
                                >
                                  <RemoveCircleOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        )}

        {/* 16. Job Description and 17. Business Requirement */}
        <Grid item container spacing={2} xs={12}>
          <Grid item xs={12} sm={6}>
            <Typography pl={0.5}>Job Description</Typography>
            <Quill
              name="jd"
              value={jdValue}
              setValue={(value) => {
                setJdValue(value);
              }}
              readonly={isViewMode}
              error={Boolean(errors.jd)}
              style={{
                height: "250px",
              }}
            />
            {errors?.jd && (
              <Typography
                sx={{
                  color: "red",
                  paddingLeft: "20px",
                  paddingTop: 1,
                }}
              >
                {errors?.jd}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography pl={0.2}>Business Requirement</Typography>
            <TextField
              fullWidth
              multiline
              name="justification_comment"
              value={formData?.justification_comment}
              onChange={handleChange}
              InputProps={{
                readOnly: isViewMode,
              }}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "292px",
                  alignItems: "flex-start",
                },
                "& textarea": {
                  height: "100% !important",
                  overflowY: "auto !important",
                },
              }}
            />
          </Grid>
        </Grid>

        {/* 18. Additional Remarks */}
        <Grid item xs={12}>
          <Typography pl={0.2}>Additional Remarks, if any</Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            name="additional_comments"
            value={formData?.additional_comments}
            onChange={handleChange}
            InputProps={{
              readOnly: isViewMode,
            }}
            size="small"
            sx={{
              "& textarea": {
                overflowY: "auto !important",
              },
            }}
          />
        </Grid>
        <Grid key="emails" item xs={12}>
          <FormControl fullWidth>
            <LabelWithInfo
                variant={"body1"}
                props={{color: "none"}}
                label="HR Leads"
                tooltip="Selected HR Leads will receive copies of MPR approval email notifications in CC."
                style={{ mb: 0.5, pl: 0.2 }}
            />
            <Box
              sx={{
                width: "100%",
                border: "1px solid #e4dfdf",
                borderRadius: "4px",
                backgroundColor: "#ffffff",
                transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  borderColor: isViewMode ? "#e4dfdf" : "rgba(0, 0, 0, 0.87)",
                },
                "&:focus-within": {
                  borderColor: "#883bd5",
                },
              }}
            >
              <MentionsInput
                style={styleMention}
                value={mentionTo}
                name="mentionTo"
                onChange={(e) => handleMentionChange(e, "mentionTo")}
                placeholder="Email"
                autoFocus={false}
                disabled={isViewMode}
              >
                <Mention
                  markup="__id__,"
                  displayTransform={(email_id) => `${email_id}, `}
                  data={mentionUsers}
                  displayName="email_id"
                  
                />
              </MentionsInput>
            </Box>
            {!editedRecord?.mpr_requests_pk && <Typography
              pl={0.5}
              mt={0.5}
              fontSize="12px"
              color="text.secondary"
            >
              Use <strong>@</strong> to mention an email or enter multiple email addresses separated by commas (,).
            </Typography>}
          </FormControl>
        </Grid>

        {/* Approver Remarks & Comments Timeline (when viewing existing MPR) */}
        {editedRecord && (
          <ApproverCommentsStatus
            editedRecord={editedRecord}
            mprMeta={mprMeta}
          />
        )}
      </Grid>
      {!editedRecord?.mpr_requests_pk && <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, p: 2 }}>
        <Button onClick={closeMode} variant="outlined" color="primary">
          Cancel
        </Button>
        <Button
          variant="contained"
          className="mpl-primary-btn"
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Box>}

      {editedRecord?.mpr_requests_pk && isApproveFlow && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, p: 2 }}>
          <Button onClick={closeMode} variant="outlined" color="primary">
            Cancel
          </Button>
          <Button
            variant="contained"
            className="mpl-primary-btn"
            onClick={() => {
                setApprovalStep(2);
                setActionType("Approved");
            }}
          >
            Proceed to Approve
          </Button>
        </Box>
      )}
      </Box>

      {/* --- APPROVAL FLOW UI --- */}
      <Box sx={{ display: approvalStep === 2 || (!isApproveFlow && initialActionType) ? "block" : "none", mt: 2, p: 2 }}>
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
          <Typography color="text.secondary" sx={{ fontSize: '15px' }}>
            Review MPR request for <b>{editedRecord?.role}</b> ({editedRecord?.mpr_no})
          </Typography>
        </Box>

        {actionType === "Approved" && (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  Headcount Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={isThirdApprover ? 3 : isSecondApprover ? 4 : 6}>
                    <TextField
                      fullWidth size="small" disabled label="Requested"
                      value={editedRecord?.headcount || ""} sx={{ backgroundColor: '#f9f9f9' }} InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  {(isSecondApprover || isThirdApprover) && (
                    <Grid item xs={isThirdApprover ? 3 : 4}>
                      <TextField
                        fullWidth size="small" disabled label="1st Approver"
                        value={firstApprovedCountDisplay} sx={{ backgroundColor: '#f9f9f9' }} InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  )}
                  {isThirdApprover && (
                    <Grid item xs={3}>
                      <TextField
                        fullWidth size="small" disabled label="2nd Approver"
                        value={secondApprovedCountDisplay} sx={{ backgroundColor: '#f9f9f9' }} InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={isThirdApprover ? 3 : isSecondApprover ? 4 : 6}>
                    <TextField
                      fullWidth type="number" size="small" required label="Approve"
                      value={approvedCount}
                      inputProps={{
                        min: 1,
                        ...(editedRecord?.hiring_type === "Replacement" ? { max: 5 } : {}),
                      }}
                      onChange={(e) => {
                        let val = e.target.value;
                        if (editedRecord?.hiring_type === "Replacement" && Number(val) > 5) {
                          val = "5";
                        }
                        if (val === "" || Number(val) >= 0) {
                          setApprovedCount(val);
                          const newCount = val === "" ? 0 : Number(val);
                          setApprovedBudget(newCount * salaryPerHead);
                        }
                        setErrors({ ...errors, approvedCount: "" });
                      }}
                      error={!!errors.approvedCount}
                      helperText={errors.approvedCount}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  Budget Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={isThirdApprover ? 3 : isSecondApprover ? 4 : 6}>
                    <TextField
                      fullWidth size="small" disabled label="Requested"
                      value={
                        editedRecord?.is_budgeted === false
                          ? "Non Budgeted"
                          : (editedRecord?.max_budget || fallbackMaxBudget || "")
                      }
                      sx={{ backgroundColor: '#f9f9f9' }} InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  {(isSecondApprover || isThirdApprover) && (
                    <Grid item xs={isThirdApprover ? 3 : 4}>
                      <TextField
                        fullWidth size="small" disabled label="1st Approver"
                        value={
                          editedRecord?.is_budgeted === false && !firstApprovedBudgetDisplay
                            ? "Non Budgeted"
                            : (firstApprovedBudgetDisplay || "")
                        }
                        sx={{ backgroundColor: '#f9f9f9' }} InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  )}
                  {isThirdApprover && (
                    <Grid item xs={3}>
                      <TextField
                        fullWidth size="small" disabled label="2nd Approver"
                        value={
                          editedRecord?.is_budgeted === false && !secondApprovedBudgetDisplay
                            ? "Non Budgeted"
                            : (secondApprovedBudgetDisplay || "")
                        }
                        sx={{ backgroundColor: '#f9f9f9' }} InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={isThirdApprover ? 3 : isSecondApprover ? 4 : 6}>
                    <TextField
                      fullWidth type={editedRecord?.is_budgeted === false ? "text" : "number"} size="small" label="Approve"
                      placeholder={editedRecord?.is_budgeted === false ? "Non Budgeted" : ""}
                      value={approvedBudget} onChange={(e) => setApprovedBudget(e.target.value)} InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Replacement Details Table for Approvers */}
            {editedRecord?.hiring_type === "Replacement" && (
              <Grid item xs={12} sx={{ mt: 1, mb: 2 }}>
                <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>
                      Replacement Details
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: "12px", color: "#64748b" }}>
                        (Max up to approved headcount: {Math.min(5, Math.max(1, Number(approvedCount) || 1))})
                      </Typography>
                      <Tooltip
                        title={
                          replacementRows.length >= Math.min(5, Math.max(1, Number(approvedCount) || 1))
                            ? `Maximum limit reached (${Math.min(5, Math.max(1, Number(approvedCount) || 1))})`
                            : "Add replacement employee"
                        }
                        arrow
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={replacementRows.length >= Math.min(5, Math.max(1, Number(approvedCount) || 1))}
                            onClick={handleAddReplacementRow}
                            sx={{ p: 0.5 }}
                          >
                            <AddCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                  <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "6px" }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, width: "60px", color: "#475569" }}>S.No</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Current Designation</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Current CTC</TableCell>
                          <TableCell sx={{ fontWeight: 600, width: "80px", textAlign: "center", color: "#475569" }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {replacementRows.map((row, idx) => {
                          const isLocked = idx < initialLockedCount;
                          return (
                            <TableRow key={idx}>
                              <TableCell sx={{ color: "#64748b", fontWeight: 500 }}>{idx + 1}</TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  fullWidth
                                  disabled={isLocked}
                                  placeholder="Enter name"
                                  value={row.name || ""}
                                  onChange={(e) => handleReplacementRowChange(idx, "name", e.target.value)}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  fullWidth
                                  disabled={isLocked}
                                  placeholder="Enter current designation"
                                  value={row.designation || ""}
                                  onChange={(e) => handleReplacementRowChange(idx, "designation", e.target.value)}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  fullWidth
                                  type="number"
                                  disabled={isLocked}
                                  placeholder="Enter current CTC"
                                  value={row.ctc || ""}
                                  onChange={(e) => handleReplacementRowChange(idx, "ctc", e.target.value)}
                                />
                              </TableCell>
                              <TableCell sx={{ textAlign: "center" }}>
                                <Tooltip
                                  title={
                                    isLocked
                                      ? "Previously submitted row cannot be removed"
                                      : "Remove Row"
                                  }
                                  arrow
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      disabled={isLocked}
                                      onClick={() => handleRemoveReplacementRow(idx)}
                                    >
                                      <RemoveCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Grid>
            )}

            <TextField
              fullWidth multiline rows={3} size="small" label="Approval Remarks"
              value={remarks} onChange={(e) => setRemarks(e.target.value)} sx={{ mb: 2 }}
            />
          </>
        )}

        {actionType === "Rejected" && (
          <TextField
            fullWidth multiline rows={4} size="small" label="Rejection Comments"
            value={remarks} onChange={(e) => setRemarks(e.target.value)}
            error={!!errors.remarks} helperText={errors.remarks} sx={{ mb: 2 }}
          />
        )}

        <Grid container spacing={2} justifyContent="flex-end">
          {(!initialActionType || isApproveFlow) && (
              <Grid item>
                <Button variant="outlined" size="medium" onClick={() => isApproveFlow ? setApprovalStep(1) : closeMode()}>
                  Back
                </Button>
              </Grid>
          )}
          <Grid item>
            <Button
              variant="outlined" size="medium" color={actionType === "Approved" ? "primary" : "error"}
              onClick={handleApproveAction}
              startIcon={isLoadingApproverMprRequests ? <CircularProgress size={18} color="inherit" /> : null}
              disabled={isLoadingApproverMprRequests}
            >
              {actionType === "Approved" ? "Approve" : "Reject"}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const styleMention = {
  ...MentionInputStyles,
  control: {
    ...MentionInputStyles?.control,
    backgroundColor: "transparent",
    width: "100%",
  },
  "&multiLine": {
    ...MentionInputStyles?.["&multiLine"],
    control: {
      ...MentionInputStyles?.["&multiLine"]?.control,
      fontFamily: "'Nunito Sans', Roboto, sans-serif",
      minHeight: "75px",
      maxHeight: "100px",
    },
    highlighter: {
      ...MentionInputStyles?.["&multiLine"]?.highlighter,
      padding: "9px 8px",
      minHeight: "75px",
      maxHeight: "100px",
      lineHeight: "20px",
      fontSize: "14px",
      fontFamily: "'Nunito Sans', Roboto, sans-serif",
      boxSizing: "border-box",
      border: "none",
      overflow: "hidden",
    },
    input: {
      ...MentionInputStyles?.["&multiLine"]?.input,
      padding: "9px 8px",
      minHeight: "75px",
      maxHeight: "100px",
      lineHeight: "20px",
      fontSize: "14px",
      fontFamily: "'Nunito Sans', Roboto, sans-serif",
      boxSizing: "border-box",
      width: "100%",
      backgroundColor: "transparent",
      outline: "none",
      border: "none",
      margin: 0,
      overflow: "auto",
    },
  },
  suggestions: {
    ...MentionInputStyles?.suggestions,
    list: {
      ...MentionInputStyles?.suggestions?.list,
      zIndex: 1400,
      position: "absolute",
      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
    },
  },
}

const filterUsersByMentionTo = (mentionString, users) => {
  if (!users?.length) return [];
  if (!mentionString || !mentionString.trim()) return users;

  const rawTokens = mentionString
    .split(/[\s,;]+/)
    .map((token) => token.replace(/^[^\w@]+|[^\w@]+$/g, "").trim().toLowerCase())
    .filter((token) => token && token.includes("@"));

  const emailRegexMatches = (mentionString.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g) || [])
    .map((e) => e.trim().toLowerCase());

  const selectedEmails = new Set([...rawTokens, ...emailRegexMatches]);

  return users.filter((user) => {
    const id = (user?.id || "").trim().toLowerCase();
    const email = (user?.email_id || "").trim().toLowerCase();
    return (!id || !selectedEmails.has(id)) && (!email || !selectedEmails.has(email));
  });
};

const getUniqueUsers = (data) => {
  const users = [
    ...(data?.approver_json || []),
    ...(data?.owner_admin_json || []),
  ];

  return [
    ...new Map(users?.map((user) => [user?.ats_users_id, user])).values(),
  ];
};
export default AddMPRPopup;